import { EntityIconMap, EntityToneMap } from '@common/Entity/constants';
import CreateJob from '@common/EntityToCreate/CreateJob';
import CreatePipeline from '@common/EntityToCreate/CreatePipeline';
import { useEntityDescription } from '@common/EntityToCreate/fabricHooks';
import { useCanEditFabric } from '@common/Fabric/useCanEditFabric';
import { ProjectSourceType } from '@common/types';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { ClockIcon, ContainerIcon, DownloadDIcon, GlobeAIcon, PipelineIcon, PlusIcon } from '@prophecy/icons';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { BaseColor, theme } from '@prophecy/theme';
import { Button, Card, Direction, FeaturedIcon, Stack, StackItem, Text, Tooltip } from '@prophecy/ui-v3';
import { focusVisibleRing } from '@prophecy/ui-v3/Button/styled';
import { Variant as FeaturedIconVariant } from '@prophecy/ui-v3/Misc/FeaturedIcon/types';
import { stopPropagation } from '@prophecy/utils/dom';
import { EXPENSIVE_CSS_CLS } from '@prophecy/utils/expensiveCss';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useEditionConfig, useIsSparkSupported } from '../../context/edition';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';

const ModelCard = styled.div`
  padding-top: ${theme.spaces.x40};
  @media (max-height: 850px) {
    padding-top: 0;
  }
  width: 100%;
  max-width: calc(1000px + ${theme.spaces.x24});

  @media (max-width: 1260px) {
    max-width: 500px;
  }
`;

const CreateEntityContainer = styled(Stack)`
  padding: 125px ${theme.spaces.x32} ${theme.spaces.x32};
  @media (max-height: 850px) {
    padding-top: ${theme.spaces.x80};
  }
`;

const ButtonCard = styled(Button)`
  &:hover {
    color: ${theme.colors.gray900};
  }
  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
    .cta-button {
      opacity: 0;
    }
  }
` as typeof Button<'link'>;

const StyledCard = styled(Card)<{ disabled: boolean }>`
  padding: 0;
  position: relative;

  .${EXPENSIVE_CSS_CLS} & {
    box-shadow: ${theme.shadows.sm} !important;
    border: none !important;
  }

  .cta-button {
    opacity: 0;
  }

  &:hover {
    .cta-button {
      opacity: ${({ disabled }) => (disabled ? 0 : 1)};
    }
    box-shadow: ${({ disabled }) => (disabled ? theme.shadows.sm : theme.shadows.md)};
  }

  .cta-button {
    ${focusVisibleRing(theme.radius.x6, 2)}
    &:focus-visible {
      opacity: 1;
    }
  }
`;

const StyledCardContent = styled.div`
  max-width: 500px;
  height: 190px;
  padding: ${theme.spaces.x24};
`;

const StyledHeading = styled(Text)`
  &:hover {
    color: ${theme.colors.gray900};
  }
`;

const StyledText = styled(Text)`
  text-align: left;
  color: ${theme.colors.gray500};

  &:hover {
    color: ${theme.colors.gray500};
  }
`;

// const StyledLearnMoreButton = styled(Button)`
//   text-decoration: underline;
// ` as typeof Button;

type Props = {
  entityName: string;
  selectEntity: (entityName: string) => void;
  icon: React.ReactNode;
  tone: BaseColor;
  label: string;
  cta?: React.ReactNode;
  disabled?: boolean;
  disabledTooltip?: string;
};

const EntityCardUI = ({
  entityName,
  selectEntity,
  icon,
  tone,
  label,
  cta,
  disabled: disabledProp,
  disabledTooltip
}: Props) => {
  const globalConfigManager = useGlobalVariables();
  const isFabricSet = globalConfigManager.getFabricStatus;
  const isFabric = isCaseInsensitiveEqual(entityName, Entity.Fabric);
  const disabled = (!isFabricSet && !isFabric) || Boolean(disabledProp);
  const entityDescription = useEntityDescription(entityName);
  const getEntityTooltipText = () => {
    if (!disabled) return null;

    if (isCaseInsensitiveEqual(entityName, Entity.Project)) {
      return 'You need at least 1 Fabric in order to create a Project.';
    }
    return disabledTooltip;
  };

  const tooltip = getEntityTooltipText();
  return (
    <Tooltip
      title={tooltip}
      placement='right'
      placementOffset={-250}
      align='start'
      alignOffset={30}
      overlayStyle={{ width: '250px' }}>
      <div tabIndex={-1}>
        <ButtonCard
          tabIndex={-1}
          variant='plain'
          disabled={disabled}
          elementType='link'
          to={Private_Routes.Create_Entity.tab.getUrl({ tab: entityName })}
          onClick={() => {
            selectEntity(entityName);
          }}
          data-test-id='create-entity-card-button'>
          <StyledCard disabled={disabled}>
            <StyledCardContent>
              <Stack gap={theme.spaces.x24} alignY='space-between' align='center'>
                <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center' align='space-between'>
                  <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                    <FeaturedIcon
                      size={'xl'}
                      tone={disabled ? BaseColor.gray : tone}
                      variant={FeaturedIconVariant.light}
                      icon={icon}
                    />
                    <StyledHeading
                      level='md'
                      weight={theme.fontWeight.medium}
                      color={disabled ? theme.colors.gray400 : theme.colors.gray900}>
                      {label}
                    </StyledHeading>
                  </Stack>
                  {cta || (
                    <Button
                      disabled={disabled}
                      variant='primary'
                      iconPlacement='left'
                      icon={<PlusIcon type='default' />}
                      className='cta-button'
                      data-test-id='create-entity-cta-button'>
                      Create
                    </Button>
                  )}
                </Stack>
                <StyledText level='sm'>{entityDescription}</StyledText>
              </Stack>
            </StyledCardContent>
          </StyledCard>
        </ButtonCard>
      </div>
    </Tooltip>
  );
};

const { icon: ModelIcon, type: ModelIconType } = EntityIconMap[Entity.Model];
function ModelCardUI() {
  const globalConfigManager = useGlobalVariables();
  const isFabricSet = globalConfigManager.getFabricStatus;
  const disabled = !isFabricSet;

  const tone = EntityToneMap[Entity.Model];

  return (
    <ModelCard>
      <Tooltip
        title={disabled ? 'You need at least 1 Fabric in order to use IDE.' : null}
        placement='right'
        placementOffset={-250}
        align='start'
        alignOffset={30}
        overlayStyle={{ width: '250px' }}>
        <ButtonCard
          tabIndex={-1}
          variant='plain'
          disabled={disabled}
          elementType='link'
          to={Private_Routes.IDE_Home.getUrl({ entity: Entity.Pipeline.toLowerCase() })}
          data-test-id='create-entity-manage-models-button'>
          <StyledCard disabled={disabled}>
            <Stack
              gap={theme.spaces.x24}
              direction='horizontal'
              padding={theme.spaces.x24}
              alignY='center'
              align='space-between'>
              <Stack direction='horizontal' alignY='center' gap={theme.spaces.x20}>
                <StackItem shrink='0'>
                  <FeaturedIcon
                    size={'xl'}
                    tone={disabled ? BaseColor.gray : tone}
                    variant={FeaturedIconVariant.light}
                    icon={<ModelIcon type={ModelIconType} />}
                  />
                </StackItem>
                <Stack gap={theme.spaces.x8}>
                  <StyledHeading level='md' color={disabled ? theme.colors.gray400 : theme.colors.gray900}>
                    Manage Models
                  </StyledHeading>
                  <StyledText level='sm'>
                    To create and modify models, seeds, functions, and gems use the development screen directly.{' '}
                    {/** TODO add it back once doc link is present. */}
                    {/* <StyledLearnMoreButton variant='plain' elementType='anchor' href='' target='_blank'>
                  Learn More.
                </StyledLearnMoreButton> */}
                  </StyledText>
                </Stack>
              </Stack>
              <StackItem shrink='0'>
                <Button
                  variant='primary'
                  iconPlacement='left'
                  className='cta-button'
                  data-test-id='manage-models-open-editor-button'>
                  Open Editor
                </Button>
              </StackItem>
            </Stack>
          </StyledCard>
        </ButtonCard>
      </Tooltip>
    </ModelCard>
  );
}
const EntityCards = () => {
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.Create_Entity.tab.url>>();
  const [entity, selectEntity] = useState(params.tab ? params.tab : '');
  const navigate = useNavigate();
  const globalConf = useGlobalVariables();

  const closePanel = (autoClosed?: boolean) => {
    if (autoClosed) return;
    navigate(Private_Routes.Create_Entity.home.getUrl());
    selectEntity('');
  };

  const [searchParams] = useSearchParams();
  const paramProjectUID = searchParams.get('projectId') || undefined;

  const getCreateProjectUrl = (projectSourceType: ProjectSourceType) => {
    return Private_Routes.Create_Entity.tab.getUrl(
      {
        tab: Entity.Project.toLowerCase()
      },
      { projectType: projectSourceType }
    );
  };

  const { allowFabricCreation: editionAllowFabricCreation, disabledMessage: editionDisabledMessage } =
    useEditionConfig();
  const isSparkSupported = useIsSparkSupported();
  const editionConfig = useEditionConfig();

  const {
    actions: { hasFabricCrudOperationAccess }
  } = useCanEditFabric();

  const { allowFabricCreation, fabricCreationDisabledReason, hideFabricCreationCard } = useMemo(() => {
    if (!hasFabricCrudOperationAccess('', false).allowed) {
      return {
        allowFabricCreation: false,
        fabricCreationDisabledReason: 'Only Cluster Admins can create new fabrics'
      };
    }

    if (!editionAllowFabricCreation) {
      return {
        allowFabricCreation: false,
        fabricCreationDisabledReason: editionDisabledMessage,
        hideFabricCreationCard: true
      };
    }

    return {
      allowFabricCreation: true
    };
  }, [hasFabricCrudOperationAccess, editionAllowFabricCreation, editionDisabledMessage]);

  return (
    <CreateEntityContainer gap={theme.spaces.x24} alignY='center'>
      <Stack gap={theme.spaces.x48} wrap width='100%' alignY='center'>
        <Stack gap={theme.spaces.x24} direction={Direction.horizontal} align='center' width='100%' wrap>
          <EntityCardUI
            entityName={Entity.Pipeline.toLowerCase()}
            selectEntity={selectEntity}
            icon={<PipelineIcon type='extended' />}
            tone={BaseColor.secondary}
            label={Entity.Pipeline}
          />
          {isSparkSupported && (
            <>
              <EntityCardUI
                entityName={Entity.Job.toLowerCase()}
                selectEntity={selectEntity}
                icon={<ClockIcon type='default' />}
                tone={BaseColor.blue}
                label={Entity.Job}
              />
            </>
          )}
          <EntityCardUI
            entityName={Entity.Project.toLowerCase()}
            selectEntity={selectEntity}
            icon={<ContainerIcon type='default' />}
            tone={BaseColor.primary}
            label={Entity.Project}
            cta={
              <Stack direction='horizontal' gap={theme.spaces.x8}>
                <Button
                  tooltip={{ title: 'Create a new project on git.', placement: 'bottom', align: 'start' }}
                  variant='primary'
                  elementType='link'
                  to={getCreateProjectUrl(ProjectSourceType.new)}
                  onClick={stopPropagation}
                  iconPlacement='left'
                  icon={<PlusIcon type='default' />}
                  className='cta-button'
                  data-test-id='create-entity-create-new-project-button'>
                  Create
                </Button>
                <Button
                  tooltip={{
                    title:
                      'Import from an existing Project on Git. An existing Project is a repository on GitHub or a folder in a repository.',
                    placement: 'bottom',
                    align: 'start',
                    style: { width: '300px' }
                  }}
                  variant='primary'
                  elementType='link'
                  to={getCreateProjectUrl(ProjectSourceType.imported)}
                  onClick={stopPropagation}
                  iconPlacement='left'
                  icon={<DownloadDIcon type='default' />}
                  className='cta-button'
                  data-test-id='create-entity-import-project-button'>
                  Import
                </Button>
              </Stack>
            }
          />
          {!hideFabricCreationCard && (
            <EntityCardUI
              entityName={Entity.Fabric.toLowerCase()}
              selectEntity={selectEntity}
              icon={<GlobeAIcon type='default' />}
              tone={BaseColor.greenLight}
              label={Entity.Fabric}
              disabled={!allowFabricCreation}
              disabledTooltip={`Fabric Creation is disabled. ${fabricCreationDisabledReason}`}
            />
          )}
        </Stack>
        {globalConf.isSQLProjectEnabled && editionConfig.isModelEnabled ? <ModelCardUI /> : null}
      </Stack>
      {entity === 'pipeline' && (
        <CreatePipeline closePanel={closePanel} projectId={paramProjectUID} isCommonCreatePipeline={true} />
      )}
      {entity === 'job' && <CreateJob closePanel={closePanel} projectId={paramProjectUID} />}
    </CreateEntityContainer>
  );
};

export default EntityCards;
